import { useMemo, useState } from "react";

import { Button, Card, Icon, Notification } from "@stellar/design-system";

import { CsvPreview } from "@/components/CsvPreview";
import { CsvUpload } from "@/components/CsvUpload";
import { CsvUploadButton } from "@/components/CsvUploadButton";

import { formatUploadedFileDisplayName } from "@/helpers/formatUploadedFileDisplayName";
import { saveFile } from "@/helpers/saveFile";
import { validateCsvFile, CsvValidationError } from "@/helpers/validateCsvFile";


import { ReceiversAside } from "./ReceiversAside";
import { TemplateInstructionsModal } from "./TemplateInstructionsModal";

import { Disbursement, NONE_VERIFICATION_VALUE } from "@/types";

interface StepReceiversProps {
  draftDetails?: Disbursement;
  csvFile?: File;
  isDraftEnabled: boolean;
  isSavePending: boolean;
  availableBalance?: string;
  futureBalance: number;
  onCsvFileChange: (file?: File) => void;
  onNext: () => void;
  onBack: () => void;
  onSaveDraft: () => void;
}

const getCsvTemplateName = (
  registrationContactType?: string,
  verificationField?: string,
): string => {
  switch (registrationContactType) {
    case "EMAIL":
    case "PHONE_NUMBER":
      if (!verificationField) return "";
      return verificationField !== NONE_VERIFICATION_VALUE
        ? `${registrationContactType}_${verificationField}`
        : `${registrationContactType}`;
    case "EMAIL_AND_WALLET_ADDRESS":
    case "PHONE_NUMBER_AND_WALLET_ADDRESS":
      return `${registrationContactType}`;
    default:
      return "";
  }
};

export const StepReceivers = ({
  draftDetails,
  csvFile,
  isDraftEnabled,
  isSavePending,
  availableBalance,
  futureBalance,
  onCsvFileChange,
  onNext,
  onBack,
  onSaveDraft,
}: StepReceiversProps) => {
  const [csvErrors, setCsvErrors] = useState<CsvValidationError[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState<boolean>(false);

  const csvTemplateName = useMemo(
    () =>
      getCsvTemplateName(draftDetails?.registrationContactType, draftDetails?.verificationField),
    [draftDetails?.registrationContactType, draftDetails?.verificationField],
  );

  const csvErrorHighlightColumns = useMemo(() => {
    if (csvErrors.length === 0) return undefined;
    const cols = [...new Set(csvErrors.map((e) => e.column).filter((c): c is string => c !== undefined))];
    return cols.length > 0 ? cols : undefined;
  }, [csvErrors]);

  const csvColumnNotes = useMemo(() => {
    const type = draftDetails?.registrationContactType;
    const verification = draftDetails?.verificationField;
    const notes: Record<string, string> = {};

    if (type === "PHONE_NUMBER" || type === "PHONE_NUMBER_AND_WALLET_ADDRESS") {
      notes.phone = "International format required, starting with + (e.g., +12025551234)";
    }

    if (type === "EMAIL" || type === "EMAIL_AND_WALLET_ADDRESS") {
      notes.email = "Must be a valid email address (e.g., user@example.com)";
    }

    if (verification && verification !== NONE_VERIFICATION_VALUE) {
      switch (verification) {
        case "DATE_OF_BIRTH":
          notes.verification = 'Valid date in "YYYY-MM-DD" format (e.g., 1990-01-15)';
          break;
        case "YEAR_MONTH":
          notes.verification = 'Valid year and month in "YYYY-MM" format (e.g., 1990-01)';
          break;
        case "PIN":
          notes.verification = "4–8 alphanumeric characters (case sensitive)";
          break;
        case "NATIONAL_ID_NUMBER":
          notes.verification = "National ID number as issued (no specific format enforced)";
          break;
      }
    }

    return Object.keys(notes).length > 0 ? notes : undefined;
  }, [draftDetails?.registrationContactType, draftDetails?.verificationField]);

  const handleDownloadTemplate = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    saveFile({
      fileUrl: csvTemplateName
        ? `/resources/disbursementTemplates/${csvTemplateName}.csv`
        : "/resources/disbursement-template.csv",
      suggestedFileName: csvTemplateName
        ? `DISBURSEMENT_TEMPLATE_${csvTemplateName}.csv`
        : "disbursement-template.csv",
    });
  };

  const getDownloadTitle = () => {
    if (csvTemplateName) return "";
    if (!draftDetails?.registrationContactType) return "Please select Registration Contact Type";
    if (["EMAIL", "PHONE_NUMBER"].includes(draftDetails.registrationContactType)) {
      return "Please select Verification type";
    }
    return "";
  };

  const handleCsvChange = async (file?: File) => {
    setCsvErrors([]);
    onCsvFileChange(file);

    if (!file) return;

    setIsValidating(true);
    const result = await validateCsvFile(
      file,
      draftDetails?.registrationContactType,
      draftDetails?.verificationField,
    );
    setIsValidating(false);
    setCsvErrors(result.errors);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!csvFile) return;

    setIsValidating(true);
    const result = await validateCsvFile(
      csvFile,
      draftDetails?.registrationContactType,
      draftDetails?.verificationField,
    );
    setIsValidating(false);

    if (!result.valid) {
      setCsvErrors(result.errors);
      return;
    }

    onNext();
  };

  const receiverState: "empty" | "uploaded" | "error" = !csvFile
    ? "empty"
    : csvErrors.length > 0
      ? "error"
      : "uploaded";

  const hasUpload = receiverState !== "empty";
  const isNextDisabled = !csvFile || isSavePending || isValidating || csvErrors.length > 0;

  const errorNotificationTitle = useMemo(() => {
    if (csvErrors.length === 0) return "Validation Errors";

    const affectedRows = [...new Set(csvErrors.map((e) => e.row).filter((r): r is number => r !== undefined))].sort((a, b) => a - b);
    const affectedCols = [...new Set(csvErrors.map((e) => e.column).filter((c): c is string => c !== undefined))];

    const colLabel =
      affectedCols.length === 0
        ? ""
        : affectedCols.length === 1
          ? ` in "${affectedCols[0]}"`
          : ` in "${affectedCols.slice(0, -1).join('", "')}" and "${affectedCols[affectedCols.length - 1]}"`;

    const rowLabel =
      affectedRows.length === 0
        ? ""
        : affectedRows.length <= 5
          ? ` — Row${affectedRows.length > 1 ? "s" : ""} ${affectedRows.join(", ")}`
          : ` — ${affectedRows.length} rows affected`;

    return `CSV Errors${colLabel}${rowLabel}`;
  }, [csvErrors]);

  const renderCardContent = () => {
    if (receiverState === "empty") {
      return (
        <CsvUpload initFile={csvFile} onChange={handleCsvChange} isDisabled={!isDraftEnabled} />
      );
    }

    return (
      <>
        {receiverState === "error" ? (
          <Notification
            variant="error"
            title={errorNotificationTitle}
            isFilled
          >
            {csvErrors.length > 0 ? (
              <ul style={{ margin: 0, paddingLeft: "1.2rem" }}>
                {csvErrors.slice(0, 10).map((err, i) => (
                  <li key={i}>{err.message}</li>
                ))}
                {csvErrors.length > 10 ? (
                  <li>…and {csvErrors.length - 10} more error(s).</li>
                ) : null}
              </ul>
            ) : (
              <>
                These recipients have received funds before and are already registered with a
                different verification method or value. Please update these rows to match the
                verification info used in their previous disbursements.
              </>
            )}
          </Notification>
        ) : null}

        <div className="DisbursementReceiversFileBar">
          <div className="DisbursementReceiversFilePill">
            <Icon.File02 />
            <span>{csvFile ? formatUploadedFileDisplayName(csvFile) : ""}</span>
          </div>
          <CsvUploadButton
            variant="secondary"
            size="sm"
            isStandalone
            onChange={handleCsvChange}
            label="Reupload CSV"
          />
        </div>

        <CsvPreview
          file={csvFile}
          highlightColumns={receiverState === "error" ? csvErrorHighlightColumns : undefined}
          columnNotes={csvColumnNotes}
        />
      </>
    );
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="DisbursementForm">
        <div className="DisbursementReceiversHead">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleDownloadTemplate}
            disabled={!csvTemplateName}
            title={getDownloadTitle()}
          >
            Download CSV template
          </Button>
        </div>

        <div
          className={`DisbursementReceiversLayout ${
            hasUpload ? "DisbursementReceiversLayout--with-aside" : ""
          }`}
        >
          <div className="DisbursementReceiversMain">
            <Card>
              <p className="DisbursementReceiversIntro">
                Upload the list of receivers you want to pay. The CSV template will automatically
                update to match the options you selected on the previous page.
              </p>
              <button
                type="button"
                className="DisbursementTemplateLink"
                onClick={() => setIsTemplateModalOpen(true)}
              >
                <Icon.InfoCircle />
                <span>Template instructions</span>
              </button>

              {renderCardContent()}
            </Card>
          </div>

          {hasUpload ? (
            <ReceiversAside
              numberOfReceivers={draftDetails?.stats?.paymentsTotalCount}
              availableBalance={availableBalance}
              disbursementAmount={draftDetails?.stats?.totalAmount}
              futureBalance={futureBalance}
              assetCode={draftDetails?.asset?.code}
              hasUpload={hasUpload}
            />
          ) : null}
        </div>

        <div className="DisbursementButtons">
          <Button
            variant="tertiary"
            size="md"
            icon={<Icon.ArrowLeft />}
            iconPosition="left"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              onBack();
            }}
            disabled={isSavePending}
          >
            Back
          </Button>
          <div className="DisbursementButtons--continue">
            <Button
              variant="tertiary"
              size="md"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                onSaveDraft();
              }}
              disabled={!isDraftEnabled || isSavePending}
              isLoading={isSavePending}
            >
              Save draft
            </Button>
            <Button
              variant="primary"
              size="md"
              icon={<Icon.ArrowRight />}
              iconPosition="right"
              type="submit"
              disabled={isNextDisabled}
              isLoading={isValidating}
            >
              Review
            </Button>
          </div>
        </div>
      </form>

      <TemplateInstructionsModal
        visible={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
      />
    </>
  );
};
